<template>
  <span :class="style.heading">AmoCRM</span>
  <div v-if="edit" :class="style.fields">
    <InputStyle
      size="M"
      placeholder="Заявка с Readds"
      description="Название заявки"
      v-model="localIntegration.nameApplication"
    />
    <div :class="style.field">
      <span :class="style.title">Воронка</span>
      <SelectBase
        :text="selectedFunnel"
        :items="funnels"
        @select="selectFunnel"
      />
    </div>
    <div :class="style.field">
      <span :class="style.title">Этап</span>
      <SelectBase
        :text="selectedState"
        :items="states"
        :disabled="!selectedFunnelKey"
        @select="selectState"
      />
    </div>
  </div>
  <div :class="style.buttons">
    <SecondaryButton text="Авторизоваться" v-if="!edit" @click="authAmo" />
    <PrimaryButton text="Сохранить" v-if="edit" @click="saveIntegration" />
    <SecondaryButton
      text="Удалить"
      :icon="trashIcon"
      v-if="edit"
      @click="$emit('delete')"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import style from "./settingIntegrations.module.scss";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import trashIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import {amoCrm} from "@/page/setting/integration/IntegrationTypes";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";
import {API} from "@/api/generated";

export default defineComponent({
  name: "SettingIntegrationAmoCRM",
  emits: ["delete", "closeModal", "amoAuthorized"],
  components: {
    SelectBase,
    InputStyle,
    SecondaryButton,
    PrimaryButton
  },
  props: {
    edit: {
      type: Boolean,
      default: false
    },
    integration: {
      type: Object as PropType<amoCrm>,
      default: () => ({})
    },
    integrationIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      style,
      trashIcon,
      selectedFunnelKey: "",
      selectedFunnel: "",
      selectedState: "",
      localIntegration: {
        ...this.integration,
        mainPipelines: this.integration?.mainPipelines || "",
        mainStatus: this.integration?.mainStatus || "",
        nameApplication: this.integration?.nameApplication || ""
      } as amoCrm
    };
  },
  async mounted() {
    if (this.edit) {
      const {code, referer} = this.$route.query;
      if (
        typeof code === "string" &&
        typeof referer === "string" &&
        localStorage.getItem("integration") === "amo"
      ) {
        const data = await API.setAmoData({
          body: {code, referer}
        });

        this.$emit("amoAuthorized", data);

        localStorage.removeItem("integration");
      }

      if (this.localIntegration?.mainPipelines) {
        this.selectedFunnelKey = String(this.localIntegration.mainPipelines);
        this.selectedFunnel = this.getFunnelName(this.selectedFunnelKey);
      }

      if (this.localIntegration?.mainStatus && this.selectedFunnelKey) {
        const status = this.localIntegration.dealList?.[
          this.selectedFunnelKey
        ]?.find(
          (s: {id: string | number}) =>
            String(s.id) === String(this.localIntegration.mainStatus)
        );

        if (status) {
          this.selectedState = status.name;
        }
      }
    }
  },
  methods: {
    selectFunnel(payload: SelectBasePayload): void {
      const key = String(payload.value ?? "");

      if (!key || !this.localIntegration?.dealList?.[key]) {
        return;
      }

      this.selectedFunnelKey = key;
      this.selectedFunnel = this.getFunnelName(key);
      this.selectedState = "";
      this.localIntegration.mainPipelines = key;
      this.localIntegration.mainStatus = "";
    },
    getFunnelName(key: string): string {
      const separatorIndex = key.lastIndexOf("_");
      return separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
    },
    selectState(payload: SelectBasePayload): void {
      if (!this.selectedFunnelKey) {
        return;
      }

      const statusId = String(payload.value ?? "");
      const status = this.localIntegration.dealList?.[
        this.selectedFunnelKey
      ]?.find((item: {id: string | number}) => String(item.id) === statusId);

      if (!status) {
        return;
      }

      this.selectedState = status.name;
      this.localIntegration.mainStatus = String(status.id);
    },
    saveIntegration(): void {
      const integration = this.getIntegration.amoCrm;

      if (!integration || !Array.isArray(integration.integration)) return;

      const value = {
        ...this.localIntegration,
        nameApplication: this.localIntegration.nameApplication?.trim() || ""
      };

      if (
        this.integrationIndex >= 0 &&
        this.integrationIndex < integration.integration.length
      ) {
        integration.integration[this.integrationIndex] = value;
      }

      this.$emit("closeModal");
    },
    authAmo() {
      localStorage.setItem("integration", "amo");

      const host = window.location.hostname;

      const IntegrationID =
        host === "readds.ru"
          ? "7a13b390-8718-4ebe-89af-1054a0baed3b"
          : "bb3a131b-d4f3-40d6-9f93-8807fade2b54";
      const redirectUri = `${window.location.origin}/user/integration`;

      localStorage.setItem("amoOauthClientId", IntegrationID);
      localStorage.setItem("amoRedirectUri", redirectUri);

      const params = new URLSearchParams({
        client_id: IntegrationID,
        mode: "popup",
        redirect_uri: redirectUri
      });

      window.location.href = `https://www.amocrm.ru/oauth?${params.toString()}`;
    }
  },
  computed: {
    getIntegration() {
      return this.$integrationStore.getIntegration;
    },
    funnels(): SelectItem[] {
      if (!this.localIntegration?.dealList) return [];

      return Object.keys(this.localIntegration.dealList).map((key) => ({
        text: this.getFunnelName(key),
        value: key,
        icon: "",
        variant: "tab",
        active: key === this.selectedFunnelKey
      }));
    },
    states(): SelectItem[] {
      if (!this.localIntegration?.dealList || !this.selectedFunnelKey) {
        return [];
      }

      return this.localIntegration.dealList[this.selectedFunnelKey].map(
        (status) => ({
          text: status.name,
          value: String(status.id),
          icon: "",
          variant: "tab",
          active: String(status.id) === String(this.localIntegration.mainStatus)
        })
      );
    }
  },
  watch: {
    integration: {
      immediate: true,
      deep: true,
      handler(newVal: amoCrm): void {
        if (!newVal) return;

        this.localIntegration = {
          ...newVal,
          mainPipelines: newVal.mainPipelines || "",
          mainStatus: newVal.mainStatus || "",
          nameApplication: newVal.nameApplication || ""
        };

        this.selectedFunnelKey = this.localIntegration.mainPipelines || "";
        this.selectedFunnel = this.selectedFunnelKey
          ? this.getFunnelName(this.selectedFunnelKey)
          : "";

        const status = this.selectedFunnelKey
          ? this.localIntegration.dealList?.[this.selectedFunnelKey]?.find(
              (item: {id: string | number}) =>
                String(item.id) === String(this.localIntegration.mainStatus)
            )
          : null;

        this.selectedState = status?.name || "";
      }
    }
  }
});
</script>
